import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import DateObject from "react-date-object"
import persian from "react-date-object/calendars/persian"
import persian_fa from "react-date-object/locales/persian_fa"

import cx from 'classix';
import SeenIcon from '../../icons/SeenIcon';
import { IMessage, IMessageBody, IMessagePost, ITopic, IUserInfo, MessageTypes, TopicTypes } from '../../types';
import { AudioMsg } from "./AudioMsgController"
import { FileMsg } from "./FileMsg"
import { ImageMsg } from "./ImageMsg"
import { NeedboxMessage, ObjectMessage } from "./NeedboxMessage"
import { VideoMsg } from "./VideoMsg"
import { TextMessage } from "./TextMessage"
import { Avatar, CircularProgress, ClickAwayListener, ListItemIcon, ListItemText, Menu, MenuItem, styled, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
import { Context } from './ChatContext';
import GIcon from '../GIcon';
import { useAtomInstance, useAtomSelector, useAtomValue } from '@zedux/react';
import { messageReceiverAtom } from '../../atoms/messageReceiverAtom';
import Confirmation from '../Confirmation';
import { userByIdSelector } from '../../atoms/userListAtom';
import { firstLetter } from '../../functions/firstLetter';
import CopyIcon from '@src/icons/CopyIcon';
import ReplyIcon from '@src/icons/ReplyIcon';
import useModalState from '@src/library/useModalState';

const formatUploadSize = (value: number | null | undefined) => {
    if (typeof value !== 'number' || Number.isNaN(value) || value < 0) return null;

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let current = value;
    let unitIndex = 0;

    while (current >= 1024 && unitIndex < units.length - 1) {
        current /= 1024;
        unitIndex += 1;
    }

    const precision = current >= 100 ? 0 : current >= 10 ? 1 : 2;
    return `${current.toFixed(precision)} ${units[unitIndex]}`;
};

// class Particle {
//     x: number;
//     y: number;

//     vx: number;
//     vy: number;
//     vFactor: number;

//     size: number;
//     fillColor: string;
//     /**
//      *
//      */
//     constructor(x, y, color, size = 5) {
//         this.x = x;
//         this.y = y;
//         this.fillColor = color;
//         this.size = size;
//         this.vx = -Math.random() * 2;
//         this.vy = -Math.random() * 2;
//         this.vFactor = 1.01;

//     }
//     draw(context: CanvasRenderingContext2D) {
//         context.fillStyle = this.fillColor;
//         context.fillRect(this.x, this.y, this.size, this.size)
//     }
//     update() {
//         this.x += this.vx;
//         this.y += this.vy;
//         this.vx *= this.vFactor;
//         this.vy *= this.vFactor;

//     }
// }
// class Generator {
//     width: number;
//     height: number;
//     particlesArray: Particle[] = [];
//     gap = 5;


//     constructor(width, height) {
//         this.width = width;
//         this.height = height;
//     }
//     init(ctx: CanvasRenderingContext2D) {
//         const pixels = ctx.getImageData(0, 0, this.width, this.height).data;
//         console.log(pixels)
//         for (let y = 0; y < this.height; y += this.gap) {
//             for (let x = 0; x < this.width; x += this.gap) {
//                 const index = (y * this.width + x) * 4;
//                 const red = pixels[index];
//                 const green = pixels[index + 1];
//                 const blue = pixels[index + 2];
//                 const alpha = pixels[index + 3];
//                 const color = `rgb(${red}, ${green}, ${blue})`;

//                 if (alpha > 0) {
//                     this.particlesArray.push(new Particle(x, y, color));
//                 }
//             }
//         }
//     }
//     draw(context) {
//         this.particlesArray.forEach((particle) => particle.draw(context))
//     }
//     update() {
//         this.particlesArray.forEach((particle) => particle.update())
//     }

// }


export default function MessagesBody({ topic, showName, message, replyClicked, setEditMode, setReplyMode, highlight }: IMessageBody) {
    const { created_at, viewer_identity_id, sender_id, text, attachment, mime_type, is_seen,
        filename, forward_fullname, forward_text, status, message_id, sender_is_me, sender_is_sys, edited_at,
        reply_id, reply_text, reply_sender_fullname, reply_sender_username, reply_message_type, message_type,
        file_content
    } = message

    const { context, userInfo } = useContext(Context)

    const { topic_id } = topic || {}

    const uploadProgress = typeof message.upload_progress === 'object' && message.upload_progress !== null
        ? message.upload_progress
        : null;
    const totalUploadBytes = message.file_content?.size
        ?? (uploadProgress ? uploadProgress.total_chunks * uploadProgress.chunk_size : null);
    const uploadedBytes = uploadProgress
        ? uploadProgress.uploaded_chunks * uploadProgress.chunk_size
        : null;
    const uploadStatusLabel = totalUploadBytes != null && uploadedBytes != null
        ? `${formatUploadSize(uploadedBytes)}/${formatUploadSize(totalUploadBytes)}`
        : null;

    const isError = status === "error"
    const isPending = status === "pending" || status === 'editing'
    const isSent = (!isError && !isPending) && message_id > 0

    const contextMenuAnchorElement = useRef<HTMLDivElement>(null)


    const dateObject = useMemo(() => new DateObject(created_at).convert(persian, persian_fa), [created_at])
    const [isOpen, setIsOpen] = useState(false)
    const [contextMenu, setContextMenu] = React.useState<boolean>(false);
    // const {  setForward } = useContext(Context)
    const messageRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const longPressTimer = useRef<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false)
    const [showDeleteConfirm, openDeleteConfirm, closeDeleteConfirm] = useModalState(false)
    const [confirmLoading, setConfirmLoading] = useState(false)

    const { resendMessage, deleteMessage } = useAtomInstance(messageReceiverAtom, [context]).exports

    const otherUserInfo = useAtomSelector(userByIdSelector, sender_id)


    const handleContextMenu = (event?: React.MouseEvent<HTMLElement> | React.PointerEvent<HTMLElement>) => {
        event?.preventDefault();
        setContextMenu(true);
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLElement>) => {
        longPressTimer.current = window.setTimeout(() => {
            handleContextMenu(event);
            if (navigator.vibrate) navigator.vibrate(50);
        }, 700);
    };

    const handlePointerUp = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };


    const handleCloseContextMenu = () => {
        setContextMenu(false);
    };

    const handleDeleteClick = () => {
        handleCloseContextMenu();
        openDeleteConfirm();
    }


    const handleDelete = async () => {
        setConfirmLoading(true)
        try {
            await deleteMessage(message.message_id, topic_id)
        } finally {
            setConfirmLoading(false)
            closeDeleteConfirm()
        }

        // TODO : this animation can be added later
        // if (!!messageRef.current) {
        //     console.log(messageRef.current)
        //     const canvas = await toCanvas(messageRef.current)
        //     console.log(messageRef.current)
        //     canvas.width = messageRef.current.offsetWidth;
        //     canvas.height = messageRef.current.offsetHeight
        //     canvasRef.current = canvas;
        //     const gen = new Generator(messageRef.current.offsetWidth, messageRef.current.offsetHeight)
        //     const ctx = canvas.getContext("2d")
        //     if (ctx) {
        //         ctx.imageSmoothingEnabled = false;
        //         gen.init(ctx)
        //         gen.draw(ctx)
        //         startAnimation(gen,ctx,canvas.width,canvas.height,0)
        //         //put pixel info to imageDataArray (Weighted Distributed)
        //     }
        // }
    }

    const handleCopyToClipboard = () => {
        navigator.clipboard.writeText(text);
    }

    if (isDeleting) {
        return
    }

    return <>
        <ClickAwayListener
            onClickAway={() => setIsOpen(false)}>
            <div className={cx('flex flex-col w-full group', !!highlight && 'bg-sky-500')}
                id={`${topic.topic_id}-${message.message_id}-message`}

            >
                {showName && <div className={cx('text-stone-400/80 text-xs flex items-center gap-2 py-1.5', sender_is_me ? 'self-start flex-row -mx-2' : 'self-end flex-row-reverse -mx-2')}>
                    <Avatar src={otherUserInfo?.avatar_url ?? ''} alt={otherUserInfo?.fullname} sx={{ width: 30, height: 30 }} >
                        {firstLetter(otherUserInfo?.fullname ? otherUserInfo.fullname[0] : '?')}
                    </Avatar>
                    {sender_is_me ? "من" : message.sender_username}
                </div>
                }

                <div className={cx('flex w-full relative items-end gap-2', sender_is_me || sender_is_sys ? "flex-row" : "flex-row-reverse")}>
                    <div
                        ref={messageRef}
                        onPointerDown={handlePointerDown}
                        onPointerUp={handlePointerUp}
                        onPointerLeave={handlePointerUp}
                        onPointerCancel={handlePointerUp}
                        onContextMenu={handleContextMenu}
                        className={
                            cx(
                                'rounded-2xl py-5 px-3 min-w-36 relative text-sm w-fit flex max-w-[70dvw] lg:max-w-96 overflow-clip select-none',
                                sender_is_me && 'self-start flex-col rounded-br-none',
                                sender_is_me && isError && "bg-error-03",
                                sender_is_me && !isError && "bg-primary-03",
                                !sender_is_me && sender_is_sys && 'bg-cyan-100 self-center flex-col-reverse rounded-bl-none rounded-tr-none',
                                !sender_is_me && !sender_is_sys && 'bg-neutral-30 flex-col rounded-bl-none',
                            )
                        }
                        
                        >
                        {
                            (!!forward_text && forward_text.length > 0) &&
                            <div className='bg-stone-200 px-2 border-r-2 border-r-stone-500 cursor-pointer'>
                                <div className='flex items-center text-sm'>
                                    <span className="mx-1 text-[0.5em]">))  </span>
                                    {!!forward_fullname ? " ادمین " : forward_fullname}</div>{forward_text}
                            </div>
                        }
                        {
                            !!reply_id &&
                            <div onClick={(e) => { replyClicked(reply_id) }} className='bg-stone-200 px-2 border-r-2 border-r-stone-500 cursor-pointer'>
                                <div className='flex items-center text-sm'>
                                    <span className="mx-1 text-[0.5em]">))  </span>
                                    {reply_sender_username === userInfo.username ? " من " : (reply_sender_fullname || reply_sender_username)}</div>
                                {reply_message_type === MessageTypes.TEXT && <div className='max-w-[50dvw] lg:max-w-xs text-sm truncate'>{reply_text}</div>}
                                {reply_message_type !== MessageTypes.TEXT && <div className='mt-1'>
                                    <GIcon name={reply_message_type === MessageTypes.IMAGE ? 'image' : reply_message_type === MessageTypes.VOICE ? 'audiotrack' : reply_message_type === MessageTypes.VIDEO ? 'videocam' : reply_message_type === MessageTypes.OBJECT ? 'inventory_2' : 'insert_drive_file'} className="material-icons-outlined" />
                                    <span className='text-black text-sm'>{reply_message_type === MessageTypes.FILE ? 'فایل' : reply_message_type === MessageTypes.VOICE ? "فایل صوتی" : reply_message_type === MessageTypes.VIDEO ? "فایل ویدئویی" : reply_message_type === MessageTypes.OBJECT ? "نیدباکس" : "فایل تصویری"}</span>
                                </div>}
                            </div>
                        }
                        {isDeleting && <canvas className='border border-black outline-accent' ref={canvasRef} />}
                        {sender_is_sys && <span className='top-1 right-1 absolute text-xxxs'>پیام مدیر</span>}
                        <div className={`w-full flex ${sender_is_me ? "flex-row" : "flex-row-reverse"} justify-between items-end overflow-clip whitespace-normal `} style={{ wordBreak: 'break-word' }}>
                            {Boolean(message_type === MessageTypes.TEXT) && <TextMessage text={text} />}
                            {Boolean(message_type === MessageTypes.VOICE) && <AudioMsg url={attachment ?? ''} />}
                            {Boolean(message_type === MessageTypes.IMAGE) && <ImageMsg url={attachment ?? ''} text={text} file={file_content} />}
                            {Boolean(message_type === MessageTypes.FILE) && <FileMsg url={attachment ?? ''} mime_type={mime_type ?? ""} text={text} filename={filename} />}
                            {Boolean(message_type === MessageTypes.VIDEO) && <VideoMsg url={attachment ?? ''} text={text} />}
                            {Boolean(message_type === MessageTypes.OBJECT) && <ObjectMessage message={message} />}
                        </div>
                        {Boolean(edited_at != null) &&
                            <div className='bottom-1 left-2 absolute text-secondary-00 text-xxs'>
                                ویرایش شده
                            </div>
                        }
                        <span className='top-1 left-2 absolute opacity-50 text-xxs' style={{ direction: 'ltr' }}>
                            {
                                dateObject.format('HH:mm')
                            }
                        </span>
                        {Boolean(isPending && uploadStatusLabel) && (
                            <span className='top-1 right-2 absolute opacity-70 text-xxs' style={{ direction: 'ltr' }}>
                                {uploadStatusLabel}
                            </span>
                        )}
                    </div>

                    {isSent && sender_is_me && <SeenIcon seen={is_seen} />}
                    {
                        Boolean(isPending) &&
                        <div className='flex items-center gap-2 min-w-12'>
                            <span className='size-3'><CircularProgress size={12} color='inherit' /></span>
                        </div>
                    }
                    {
                        Boolean(isError) &&
                        <span className='stroke-red-950 size-4 hover:rotate-180 cursor-pointer'
                            onClick={() => resendMessage(topic_id, message.message_id)}>
                            <RetryIcon />
                        </span>
                    }
                    {!sender_is_me &&
                        <div className='lg:hidden stroke-2 stroke-neutral-90 mb-0.5 size-4 cursor-pointer'
                            onClick={async () => {
                                setReplyMode(message)
                                handleCloseContextMenu()
                            }}>
                            <ReplyIcon />
                        </div>
                    }
                    <div className='hidden group-hover:flex'>
                        <GIcon ref={contextMenuAnchorElement} onClick={handleContextMenu} className='self-end mx-1 text-sm cursor-pointer' name="info" />
                    </div>
                </div>
            </div>
        </ClickAwayListener>
        {
            <Menu
                open={contextMenu}
                onClose={handleCloseContextMenu}
                anchorEl={contextMenuAnchorElement.current}
                disableScrollLock
            >
                <MenuItem onClick={() => { navigator.clipboard.writeText(text); handleCloseContextMenu() }}>
                    <ListItemIcon><GIcon name="assignment" /></ListItemIcon>
                    <ListItemText><span>کپی</span></ListItemText>
                </MenuItem>
                {Boolean(!is_seen && sender_is_me) &&
                    <MenuItem onClick={() => {
                        setEditMode(message)
                        handleCloseContextMenu()
                    }}>
                        <ListItemIcon><GIcon name="edit" /></ListItemIcon>
                        <ListItemText><span>ویرایش</span></ListItemText>
                    </MenuItem>
                }
                {Boolean(!is_seen && sender_is_me) &&
                    <MenuItem onClick={handleDeleteClick}>
                        <ListItemIcon><GIcon name="delete" /></ListItemIcon>
                        <ListItemText><span>حذف</span> </ListItemText>
                    </MenuItem>
                }

                <MenuItem onClick={async () => {
                    setReplyMode(message)
                    handleCloseContextMenu()
                }}>
                    <ListItemIcon><GIcon name='reply' /></ListItemIcon>
                    <ListItemText><span>جواب دادن</span></ListItemText>
                </MenuItem>
                {/* <MenuItem onClick={() => {
                    setForward(message); handleCloseContextMenu();
                }}>
                    <ListItemIcon><GIcon name='forward' /></ListItemIcon>
                    <ListItemText>فوروارد</ListItemText>
                </MenuItem> */}
            </Menu>
        }
        <Confirmation
            open={showDeleteConfirm}
            onClose={closeDeleteConfirm}
            title='حذف پیام'
            body='آیا از حذف این پیام اطمینان دارید؟'
            onSubmit={handleDelete}
            loading={confirmLoading}
        />

    </>
}

const RetryIcon = () => <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 21A9 9 0 0 1 5.3 6L8 3m4 0a9 9 0 0 1 6.7 15L16 21M3 3h5m0 0v5m13 13h-5m0 0v-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
</svg>

const ExclamationIcon = () => <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
</svg>